var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faArrowLeft, faClock, faEdit, faEye, faHdd, faPlay, faSitemap } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BButtonGroup, BTab, BTabs } from "bootstrap-vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAnimationFrameResizeObserver } from "@/composables/sensors/animationFrameResizeObserver";
import { useInvocationStore } from "@/stores/invocationStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import localize from "@/utils/localization";
import { cancelWorkflowScheduling } from "./services";
import { isTerminal, jobCount, runningCount } from "./util";
import Heading from "../Common/Heading.vue";
import SwitchToHistoryLink from "../History/SwitchToHistoryLink.vue";
import UtcDate from "../UtcDate.vue";
import InvocationReport from "../Workflow/InvocationReport.vue";
import WorkflowInvocationsCount from "../Workflow/WorkflowInvocationsCount.vue";
import WorkflowRunButton from "../Workflow/WorkflowRunButton.vue";
import WorkflowInvocationExportOptions from "./WorkflowInvocationExportOptions.vue";
import WorkflowInvocationInputOutputTabs from "./WorkflowInvocationInputOutputTabs.vue";
import WorkflowInvocationOverview from "./WorkflowInvocationOverview.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowInvocationState',
    props: {
        invocationId: null,
        index: { default: undefined },
        isSubworkflow: { type: Boolean, default: false },
        isFullPage: { type: Boolean },
        fromPanel: { type: Boolean }
    },
    emits: ["invocation-cancelled"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faArrowLeft, faClock, faEdit, faEye, faHdd, faPlay, faSitemap);
        const invocationStore = useInvocationStore();
        const stepStatesInterval = ref(undefined);
        const jobStatesInterval = ref(undefined);
        // after the report tab is first activated, no longer lazy-render it from then on
        const reportActive = ref(false);
        const reportLazy = ref(true);
        watch(() => reportActive.value, (newValue) => {
            if (newValue) {
                reportLazy.value = false;
            }
        });
        const invocationTabs = ref();
        const scrollableDiv = computed(() => { var _a; return (_a = invocationTabs.value) === null || _a === void 0 ? void 0 : _a.$el.querySelector(".tab-content"); });
        const isScrollable = ref(false);
        useAnimationFrameResizeObserver(scrollableDiv, ({ clientSize, scrollSize }) => {
            isScrollable.value = scrollSize.height >= clientSize.height + 1;
        });
        const invocation = computed(() => invocationStore.getInvocationById(props.invocationId));
        const invocationState = computed(() => { var _a; return ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.state) || "new"; });
        const invocationAndJobTerminal = computed(() => invocationSchedulingTerminal.value && jobStatesTerminal.value);
        const invocationSchedulingTerminal = computed(() => {
            return (invocationState.value == "scheduled" ||
                invocationState.value == "cancelled" ||
                invocationState.value == "failed");
        });
        const jobStatesTerminal = computed(() => {
            if (invocationSchedulingTerminal.value && jobCount(jobStatesSummary.value) === 0) {
                // no jobs for this invocation (think subworkflow or just inputs)
                return true;
            }
            return !!jobStatesSummary.value && isTerminal(jobStatesSummary.value);
        });
        const jobStatesSummary = computed(() => {
            const jobsSummary = invocationStore.getInvocationJobsSummaryById(props.invocationId);
            return (!jobsSummary ? null : jobsSummary);
        });
        const invocationStateSuccess = computed(() => {
            return (invocationState.value == "scheduled" &&
                runningCount(jobStatesSummary.value) === 0 &&
                invocationAndJobTerminal.value);
        });
        const workflowStore = useWorkflowStore();
        const isDeletedWorkflow = computed(() => { var _a; return ((_a = getWorkflow()) === null || _a === void 0 ? void 0 : _a.deleted) === true; });
        const workflowVersion = computed(() => { var _a; return (_a = getWorkflow()) === null || _a === void 0 ? void 0 : _a.version; });
        onMounted(() => {
            pollStepStatesUntilTerminal();
            pollJobStatesUntilTerminal();
        });
        onUnmounted(() => {
            clearTimeout(stepStatesInterval.value);
            clearTimeout(jobStatesInterval.value);
        });
        function pollStepStatesUntilTerminal() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!invocation.value || !invocationSchedulingTerminal.value) {
                    yield invocationStore.fetchInvocationForId({ id: props.invocationId });
                    stepStatesInterval.value = setTimeout(pollStepStatesUntilTerminal, 3000);
                }
            });
        }
        function pollJobStatesUntilTerminal() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!jobStatesTerminal.value) {
                    yield invocationStore.fetchInvocationJobsSummaryForId({ id: props.invocationId });
                    jobStatesInterval.value = setTimeout(pollJobStatesUntilTerminal, 3000);
                }
            });
        }
        function onError(e) {
            console.error(e);
        }
        function onCancel() {
            emit("invocation-cancelled");
        }
        function cancelWorkflowSchedulingLocal() {
            cancelWorkflowScheduling(props.invocationId).then(onCancel).catch(onError);
        }
        function getWorkflow() {
            var _a, _b;
            return ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.workflow_id)
                ? workflowStore.getStoredWorkflowByInstanceId((_b = invocation.value) === null || _b === void 0 ? void 0 : _b.workflow_id)
                : undefined;
        }
        function getWorkflowId() {
            var _a, _b;
            return ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.workflow_id)
                ? workflowStore.getStoredWorkflowIdByInstanceId((_b = invocation.value) === null || _b === void 0 ? void 0 : _b.workflow_id)
                : undefined;
        }
        function getWorkflowName() {
            var _a;
            return workflowStore.getStoredWorkflowNameByInstanceId((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.workflow_id);
        }
        return { __sfc: true, props, emit, invocationStore, stepStatesInterval, jobStatesInterval, reportActive, reportLazy, invocationTabs, scrollableDiv, isScrollable, invocation, invocationState, invocationAndJobTerminal, invocationSchedulingTerminal, jobStatesTerminal, jobStatesSummary, invocationStateSuccess, workflowStore, isDeletedWorkflow, workflowVersion, pollStepStatesUntilTerminal, pollJobStatesUntilTerminal, onError, onCancel, cancelWorkflowSchedulingLocal, getWorkflow, getWorkflowId, getWorkflowName, faArrowLeft, faClock, faEdit, faHdd, faSitemap, FontAwesomeIcon, BAlert, BButton, BButtonGroup, BTab, BTabs, localize, Heading, SwitchToHistoryLink, UtcDate, InvocationReport, WorkflowInvocationsCount, WorkflowRunButton, WorkflowInvocationExportOptions, WorkflowInvocationInputOutputTabs, WorkflowInvocationOverview, LoadingSpan };
    }
});
